import { Component, OnInit, Input } from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { ModalController} from '@ionic/angular';

import { Contact } from '../models/contact';
import { Property } from '../models/property';
import { Appointment } from '../models/appointment';

import { ApiService } from '../services/api.service';

import { Router } from '@angular/router';

import { ContactsSelectorComponent } from '../contacts-selector/contacts-selector.component';
import { BusinessSelectorComponent } from '../business-selector/business-selector.component';
import { LocalNotificationService } from '../services/local-notification.service';

import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { REMINDER_LABELS } from '../shared/reminder-map.constant';

@Component({
  selector: 'app-appointments-new',
  templateUrl: './appointments-new.page.html',
  styleUrls: ['./appointments-new.page.scss'],
  providers: []
})
export class AppointmentsNewPage implements OnInit {

  appt = {
    allDay: false
  }

  chosenContact: Contact;
  isNativePlatform = true;
  ionicForm: UntypedFormGroup;
  isSubmitted = false;
  properties = [];

  reminderLabels = REMINDER_LABELS;
  reminders: any[] = [];
  reminderOptions: { key: number; label: string }[] = [];

  @Input() public currentSelectedDate: Date;
  @Input() jobId?: string;  // Optional property for Job ID

  selectOptions = {
    header: 'Reminder Times',
    subHeader: 'Choose one or more reminder times for your appointment',
    message: '',
  };

  constructor(
    public modalCtrl: ModalController, 
    public formBuilder: UntypedFormBuilder, 
    public apiService: ApiService, 
    public router: Router,
    public localNotificationService: LocalNotificationService) { }

  ngOnInit() {
    this.isNativePlatform = Capacitor.isNativePlatform();

    // Convert REMINDER_LABELS into an array of sorted options
    this.reminderOptions = Object.entries(REMINDER_LABELS)
      .map(([key, value]) => ({ key: parseInt(key, 10), label: value }))
      .sort((a, b) => a.key - b.key);

    // If currentSelectedDate is provided, use it; otherwise, use the current date.
    var selectedDate = this.currentSelectedDate ? this.currentSelectedDate : new Date();
    var now = new Date();

    // Set the start time to the current time but keep the year, month, and day of the selected date.
    var startTime = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());

    // Set the end time to +1 hour from the start time.
    var endTime = new Date(startTime.getTime() + (60*60*1000));

    // Build Appointment Form
    this.ionicForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      description: '',
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      contactId: null,
      businessId: null,
      propertyId: null,
      jobId: null,
      allDay: false,
      contact: {},
      business: {},
      property: {},
      job: {}
    })

    // Listen for start time changes and update end time
    this.ionicForm.get("startTime").valueChanges.subscribe(newValue => {

      // Create a date and transform by +1 hour
      var endTime = new Date();
      endTime.setTime(new Date(newValue).getTime() + (60*60*1000));

       this.ionicForm.patchValue(
        {
          endTime: endTime.toISOString(),
        }
      );
    });

    // Load Job details if provided
    if (this.jobId) {

      this.apiService.getJob(this.jobId).subscribe(response => {
        // Handle the fetched job details
        const existingJob = response;

        console.log(existingJob);
        // Update form values
        this.ionicForm.patchValue(
          {
            jobId: existingJob.id,
            job: existingJob
          });
      },
      error => {
        // Handle any errors
        console.log(error);
      });
      
    }
  }

  dismiss() {  
    this.modalCtrl.dismiss();
  }

  async chooseCustomer() {
    const modal = await this.modalCtrl.create({  
      component: ContactsSelectorComponent,
      canDismiss: true,
      componentProps: {  },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {

      // Pre-populate form value ID
      this.ionicForm.patchValue(
        {
          contactId: data.id
        }
      );

      // Set the chosen contact data for displaying
      this.chosenContact = data;

      // Fetch properties and pre-populate the property details from the contact
      this.apiService.getPropertyListByContact(data.id).subscribe(response => {

        // If there are no properties, then we should ensure that the form is reset for properties
        this.ionicForm.patchValue(
        {
          propertyId: null,
          property: {}
        });

        // Create a properties array for the picker with ID and address 1
        this.properties = response.map(function(p) {
          return {
            text: p.address1,
            value: p.id
          }
        });

      })
    }
  }

  onReminderChange(event: any) {
    this.reminders = event.detail.value;
  }

  get displayReminders(): string {
    if (this.reminders.length > 0) {
      let str ='Alert ';
      return str+this.reminders
        .map((r) => this.reminderOptions.find((opt) => opt.key === r)?.label)
        .join(', ')+ ' before';
    }
    return 'Add Reminders';
  }

  get startTimeDisplay() {
    if (this.ionicForm.get('startTime').value) {
      return this.ionicForm.get('startTime').value;
    }
    else {
      return 'no start time chosen';
    }
  }

  get endTimeDisplay() {
    if (this.ionicForm.get('endTime').value) {
      return this.ionicForm.get('endTime').value;
    }
    else {
      return 'no end time chosen';
    }
  }

  toggleAllDay($event: CustomEvent) {
    // Patch the form
    this.ionicForm.patchValue(
    {
      allDay: $event.detail.checked
    });
  }

  private prepareSave(): Appointment {
    const newAppointment = new Appointment().deserialize(this.ionicForm.value);

    newAppointment.reminders = this.reminders; // Attach updated reminders

    return newAppointment;
  }

  async submitForm() {
    this.isSubmitted = true;

    console.log(this.ionicForm);

    if (!this.ionicForm.valid) {
      console.log('Please provide all the required values for an appointment.')
      return false;
    } else {

      // Save contact information
      const newAppointment = this.prepareSave(); // `newInvoice` is now an instance of "Invoice"

      // API Call
      this.apiService.createAppointment(newAppointment).subscribe((response) => {
        console.log(response);

        // Refresh Reminders/notifications
        this.localNotificationService.refreshNotifications();

        // Close the modal
        this.modalCtrl.dismiss(this.ionicForm.value); // TODO: Return data, although we should refresh previous view / form
      });

    }
  }

  get errorControl() {
    return this.ionicForm.controls;
  }

}
